// Authentik OAuth2/OIDC configuration for Forge Web
// Same Authentik application as Forge Studio Desktop — different redirect URI

export const AUTH_CONFIG = {
  authority: 'https://authentik.vinayakrobotics.com',
  clientId: 'yhPBa2vDLpfAxMnbBwi5fBanlVaMJO9XJ8PhwaRo',
  redirectUri: `${window.location.origin}/auth/callback`,
  postLogoutRedirectUri: window.location.origin,
  authorizationEndpoint: 'https://authentik.vinayakrobotics.com/application/o/authorize/',
  tokenEndpoint: 'https://authentik.vinayakrobotics.com/application/o/token/',
  userinfoEndpoint: 'https://authentik.vinayakrobotics.com/application/o/userinfo/',
  scopes: 'openid email profile groups goauthentik.io/api',
};

// PKCE helpers
function generateRandomString(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return hash;
}

function base64urlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  bytes.forEach((b) => { str += String.fromCharCode(b); });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function generatePKCE() {
  const codeVerifier = generateRandomString(64);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64urlEncode(hashed);
  return { codeVerifier, codeChallenge };
}

export async function startLogin() {
  const { codeVerifier, codeChallenge } = await generatePKCE();
  sessionStorage.setItem('pkce_code_verifier', codeVerifier);
  sessionStorage.setItem('auth_return_to', window.location.pathname);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: AUTH_CONFIG.clientId,
    redirect_uri: AUTH_CONFIG.redirectUri,
    scope: AUTH_CONFIG.scopes,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${AUTH_CONFIG.authorizationEndpoint}?${params.toString()}`;
}

export async function handleCallback(code) {
  const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
  if (!codeVerifier) throw new Error('No PKCE verifier found');

  const response = await fetch(AUTH_CONFIG.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AUTH_CONFIG.clientId,
      code,
      redirect_uri: AUTH_CONFIG.redirectUri,
      code_verifier: codeVerifier,
    }),
  });

  if (!response.ok) throw new Error('Token exchange failed');
  const tokens = await response.json();
  sessionStorage.removeItem('pkce_code_verifier');

  // Fetch user info
  const userResponse = await fetch(AUTH_CONFIG.userinfoEndpoint, {
    headers: { Authorization: `Bearer ${tokens.access_token}` },
  });

  if (!userResponse.ok) throw new Error('Userinfo fetch failed');
  const user = await userResponse.json();

  // Parse token expiry from JWT payload
  let expiresAt = null;
  try {
    const payload = JSON.parse(atob(tokens.access_token.split('.')[1]));
    expiresAt = payload.exp * 1000; // Convert to ms
  } catch {
    // Fallback: assume 5 minute lifetime
    expiresAt = Date.now() + 5 * 60 * 1000;
  }

  const session = {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    idToken: tokens.id_token,
    expiresAt,
    user: {
      sub: user.sub,
      name: user.name || user.preferred_username,
      username: user.preferred_username,
      email: user.email,
      avatar: user.picture || null,
      groups: user.groups || [],
    },
  };

  localStorage.setItem('forge_session', JSON.stringify(session));
  return session;
}

export function getSession() {
  const raw = localStorage.getItem('forge_session');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Refresh the access token using the refresh token
async function refreshAccessToken() {
  const session = getSession();
  if (!session?.refreshToken) return null;

  try {
    const response = await fetch(AUTH_CONFIG.tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: AUTH_CONFIG.clientId,
        refresh_token: session.refreshToken,
      }),
    });

    if (!response.ok) return null;
    const tokens = await response.json();

    let expiresAt = null;
    try {
      const payload = JSON.parse(atob(tokens.access_token.split('.')[1]));
      expiresAt = payload.exp * 1000;
    } catch {
      expiresAt = Date.now() + 5 * 60 * 1000;
    }

    const updated = {
      ...session,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token || session.refreshToken,
      idToken: tokens.id_token || session.idToken,
      expiresAt,
    };

    localStorage.setItem('forge_session', JSON.stringify(updated));
    return updated;
  } catch {
    return null;
  }
}

// Get a valid access token, refreshing if needed
// Refreshes 30 seconds before expiry to avoid race conditions
export async function getValidAccessToken() {
  const session = getSession();
  if (!session?.accessToken) return null;

  const bufferMs = 30 * 1000;
  if (session.expiresAt && Date.now() > session.expiresAt - bufferMs) {
    const refreshed = await refreshAccessToken();
    return refreshed?.accessToken || null;
  }

  return session.accessToken;
}

export function logout() {
  localStorage.removeItem('forge_session');
  window.location.href = AUTH_CONFIG.postLogoutRedirectUri;
}
